import { ComponentType, FC, useCallback } from 'react';
import type { CellClickedEvent, GridOptions } from 'ag-grid-community';

type KnownProps = Pick<
  GridOptions,
  | 'suppressClickEdit'
  | 'stopEditingWhenCellsLoseFocus'
  | 'enterNavigatesVerticallyAfterEdit'
  | 'onCellClicked'
>;

type CellClickedHandler = (event: CellClickedEvent) => boolean;

type SingleClickEditProps = {
  onCellClick?: CellClickedHandler;
};

const isEditable = (event: CellClickedEvent) => {
  const { colDef } = event;
  const { editable } = colDef;

  if (editable == null) {
    return false;
  }

  return typeof editable === 'boolean' ? editable : editable(event);
};

export const startEditing = ({
  api,
  column,
  rowIndex,
}: Pick<CellClickedEvent, 'api' | 'column' | 'rowIndex'>) => {
  api.startEditingCell({
    colKey: column,
    rowIndex: rowIndex!,
  });
};

export const withSingleClickEdit = <T extends KnownProps>(
  Component: ComponentType<React.PropsWithChildren<T>>,
): FC<
  React.PropsWithChildren<Omit<T, keyof KnownProps> & SingleClickEditProps>
> => {
  const Next: FC<
    React.PropsWithChildren<Omit<T, keyof KnownProps> & SingleClickEditProps>
  > = ({ onCellClick: onClicked, ...props }) => {
    const onCellClicked = useCallback(
      (event: CellClickedEvent) => {
        const handled = onClicked?.(event) ?? false;

        if (!handled && isEditable(event)) {
          startEditing(event);
        }
      },
      [onClicked],
    );

    return (
      <Component
        {...(props as any)}
        suppressClickEdit
        enterNavigatesVerticallyAfterEdit
        onCellClicked={onCellClicked}
      />
    );
  };

  return Next;
};
